import { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { Home, LayoutGrid, MessageSquare, Menu as MenuIcon } from 'lucide-react';
import useAuthStore from '../store/useAuthStore';
import useShiftsStore from '../store/useShiftsStore';
import chatApi from '../services/chatApi';
import chatWebsocket from '../services/chatWebsocket';
import './Sidebar.css';

function SidebarIcon({ icon, label, active, badge, onClick, to }) {
  const commonClass = `sidebar-icon-btn${active ? ' sidebar-icon-btn--active' : ''}`;

  const content = (
    <div title={label} className={commonClass} onClick={onClick}>
      <div className="sidebar-icon-inner">
        {icon}
        {badge > 0 && (
          <span className="sidebar-icon-badge">{badge > 99 ? '99+' : badge}</span>
        )}
      </div>
    </div>
  );

  if (to) {
    return (
      <NavLink to={to} className={() => commonClass} title={label}>
        <div className="sidebar-icon-inner">
          {icon}
          {badge > 0 && (
            <span className="sidebar-icon-badge">{badge > 99 ? '99+' : badge}</span>
          )}
        </div>
      </NavLink>
    );
  }

  return content;
}

function Sidebar() {
  const { user, isShiftRequired, can } = useAuthStore();
  const { activeShifts } = useShiftsStore();
  const navigate = useNavigate();
  const location = useLocation();
  const shiftRequired = isShiftRequired();
  const hasShift = !shiftRequired || activeShifts.length > 0;
  const feedActive = location.pathname.startsWith('/feed');
  const canViewFeed = can('feed', 'view');
  const chatActive = location.pathname.startsWith('/chat');
  const homeActive = location.pathname === '/';
  const menuActive = location.pathname.startsWith('/menu');
  const [unreadCount, setUnreadCount] = useState(0);
  const cancelledRef = useRef(false);

  const fetchUnread = useRef(async () => {
    try {
      const data = await chatApi.getUnreadCount();
      if (!cancelledRef.current) setUnreadCount(data?.count || 0);
    } catch {}
  });

  useEffect(() => {
    if (!user) return;
    cancelledRef.current = false;
    // Сбрасываем authFailed актуальным токеном
    const token = localStorage.getItem('token');
    if (token) chatWebsocket.setToken(token);
    chatWebsocket.connect();
    fetchUnread.current();

    const handleNewMessage = () => fetchUnread.current();
    chatWebsocket.on('new_message', handleNewMessage);
    return () => {
      cancelledRef.current = true;
      chatWebsocket.off('new_message', handleNewMessage);
    };
  }, [user]);

  useEffect(() => {
    if (!user) return;
    if (chatActive) setUnreadCount(0);
    else fetchUnread.current();
  }, [chatActive, user]);

  return (
    <aside className="hidden md:flex flex-col w-[72px] bg-[#1a222c] text-slate-400 items-center py-4 flex-shrink-0 z-20 shadow-xl">
      {/* Основные пункты навигации */}
      <div className="flex flex-col gap-1 w-full px-2 mt-2">
        <SidebarIcon
          to="/"
          icon={<Home size={22} />}
          label="Главная"
          active={homeActive}
        />
        {canViewFeed && (
          <button
            type="button"
            title="Лента"
            className={`sidebar-icon-btn${feedActive ? ' sidebar-icon-btn--active' : ''}${!hasShift ? ' sidebar-icon-btn--disabled' : ''}`}
            onClick={() => {
              if (!hasShift) return;
              navigate('/feed');
            }}
          >
            <div className="sidebar-icon-inner">
              <LayoutGrid size={22} />
            </div>
          </button>
        )}
        <SidebarIcon
          to="/chat"
          icon={<MessageSquare size={22} />}
          label="Чат"
          active={chatActive}
          badge={unreadCount}
        />
      </div>

      {/* Меню внизу */}
      <div className="mt-auto flex flex-col gap-1 w-full px-2">
        <SidebarIcon
          to="/menu"
          icon={<MenuIcon size={22} />}
          label="Меню"
          active={menuActive}
        />
      </div>
    </aside>
  );
}

export default Sidebar;
